"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.CreateOrUpdateEnvironmentFileDriver = void 0;
const tslib_1 = require("tslib");
const dotenv = tslib_1.__importStar(require("dotenv"));
const fs = tslib_1.__importStar(require("fs-extra"));
const os = tslib_1.__importStar(require("os"));
const path = tslib_1.__importStar(require("path"));
const typedi_1 = require("typedi");
const lib_1 = require("@feathersjs/hooks/lib");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const localizeUtils_1 = require("../../../common/localizeUtils");
const common_1 = require("../../utils/common");
const constants_1 = require("../aad/utility/constants");
const addStartAndEndTelemetry_1 = require("../middleware/addStartAndEndTelemetry");
const common_2 = require("../../../error/common");
const actionName = "file/createOrUpdateEnvironmentFile";
const helpLink = "https://aka.ms/teamsfx-actions/file-createOrUpdateEnvironmentFile";
let CreateOrUpdateEnvironmentFileDriver = class CreateOrUpdateEnvironmentFileDriver {
    constructor() {
        this.description = localizeUtils_1.getLocalizedString("driver.file.createOrUpdateEnvironmentFile.description");
        this.progressTitle = localizeUtils_1.getLocalizedString("driver.file.progressBar.env");
    }
    async run(args, context) {
        return common_1.wrapRun(async () => {
            const result = await this.handler(args, context);
            return result.output;
        }, actionName);
    }
    async execute(args, ctx) {
        let summaries = [];
        const outputResult = await common_1.wrapRun(async () => {
            const result = await this.handler(args, ctx);
            summaries = result.summaries;
            return result.output;
        }, actionName);
        return {
            result: outputResult,
            summaries,
        };
    }
    async handler(args, context) {
        var _a, _b, _c, _d;
        try {
            this.validateArgs(args);
            const target = this.getAbsolutePath(args.target, context.projectPath);
            await fs.ensureFile(target);
            const envs = dotenv.parse(await fs.readFile(target));
            (_a = context.logProvider) === null || _a === void 0 ? void 0 : _a.debug(`Existing envs: ${JSON.stringify(envs)}`);
            const updatedEnvs = Object.entries(Object.assign(Object.assign({}, envs), args.envs)).map(([key, value]) => `${key}=${value}`);
            (_b = context.logProvider) === null || _b === void 0 ? void 0 : _b.debug(`Updated envs: ${JSON.stringify(updatedEnvs)}`);
            await fs.writeFile(target, updatedEnvs.join(os.EOL));
            return {
                output: new Map(),
                summaries: [
                    localizeUtils_1.getLocalizedString("driver.file.createOrUpdateEnvironmentFile.summary", path.normalize(target)),
                ],
            };
        }
        catch (error) {
            if (error instanceof teamsfx_api_1.UserError || error instanceof teamsfx_api_1.SystemError) {
                (_c = context.logProvider) === null || _c === void 0 ? void 0 : _c.error(localizeUtils_1.getLocalizedString(constants_1.logMessageKeys.failExecuteDriver, actionName, error.displayMessage));
                throw error;
            }
            const message = JSON.stringify(error);
            (_d = context.logProvider) === null || _d === void 0 ? void 0 : _d.error(localizeUtils_1.getLocalizedString(constants_1.logMessageKeys.failExecuteDriver, actionName, message));
            throw new common_2.UnhandledError(error, actionName);
        }
    }
    validateArgs(args) {
        const invalidParameters = [];
        if (!args.target || typeof args.target !== "string" || args.target.length === 0) {
            invalidParameters.push("target");
        }
        if (!args.envs || typeof args.envs !== "object") {
            invalidParameters.push("envs");
        }
        else {
            for (const value of Object.values(args.envs)) {
                if (value === undefined || value === null || typeof value === "object") {
                    invalidParameters.push("envs");
                }
            }
        }
        if (invalidParameters.length > 0) {
            throw new common_2.InvalidActionInputError(actionName, invalidParameters, helpLink);
        }
    }
    getAbsolutePath(relativeOrAbsolutePath, projectPath) {
        return path.isAbsolute(relativeOrAbsolutePath)
            ? relativeOrAbsolutePath
            : path.join(projectPath, relativeOrAbsolutePath);
    }
};
tslib_1.__decorate([
    lib_1.hooks([addStartAndEndTelemetry_1.addStartAndEndTelemetry(actionName, actionName)]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], CreateOrUpdateEnvironmentFileDriver.prototype, "run", null);
tslib_1.__decorate([
    lib_1.hooks([addStartAndEndTelemetry_1.addStartAndEndTelemetry(actionName, actionName)]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], CreateOrUpdateEnvironmentFileDriver.prototype, "execute", null);
CreateOrUpdateEnvironmentFileDriver = tslib_1.__decorate([
    typedi_1.Service(actionName) // DO NOT MODIFY the service name
], CreateOrUpdateEnvironmentFileDriver);
exports.CreateOrUpdateEnvironmentFileDriver = CreateOrUpdateEnvironmentFileDriver;
//# sourceMappingURL=createOrUpdateEnvironmentFile.js.map